import { Box, Divider, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState, FC } from 'react';
import { ICandidate } from 'wisestep-api';

import { formattedDate } from '../../helper/DateOperations';
import { formatLocation } from '../../helper/LocationUtil/LocationUtil';
import { displayValue, getSalary } from '../CandidateProfileTab/CandidateProfileTab';
import CandidateWorkHistory from '../CandidateWorkHistory/CandidateWorkHistory';
import Label from '../Label/Label';
import WsChip, { WsChipVariants } from '../WsChip/WsChip';
import WsTypography, { WsTypoColors } from '../WsTypography/WsTypography';

interface IUpdatedDetailsTimeline {
  candidate: ICandidate;
  getFieldSettings: (isMapping: boolean) => any;
}
interface IGridProps {
  header: string;
  description: string;
}
export const StyledGrid = ({ header, description }: IGridProps) => (
  <Grid container>
    <Grid item xs={7}>
      <Label>{header}</Label>
    </Grid>
    <Grid item xs={1}>
      :
    </Grid>
    <Grid item xs={4}>
      <Label>{description}</Label>
    </Grid>
    <Divider sx={{ width: '100%' }} />
  </Grid>
);

const GetLabel: FC<{ title: string; mt?: number }> = ({ title, children }) => (
  <Grid container mt={2.5}>
    <Grid item xs={5.5}>
      <Label sx={{ textAlign: 'center' }}>{title}</Label>
    </Grid>
    <Grid item xs={1}>
      <Label> :</Label>
    </Grid>
    <Grid item xs={5.5} justifyContent="center">
      {children}
    </Grid>
    <Divider sx={{ width: '100%' }} />
  </Grid>
);

const allowedContractTypes = [
  'Contract',
  'Temporary',
  'Internship',
  'Freelance',
  'ContractToHireW2',
  'ContractW2',
  'ContractIndependent',
  'ContractToHireIndependent',
  'ContractCorpToCorp',
  'ContractToHireCorpToCorp',
  'PartTime',
  'FixedTermContract',
];

const UpdatedDetailsTimeline = ({ candidate, getFieldSettings }: IUpdatedDetailsTimeline) => {
  const [settings, setSettings] = useState<any>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      const req = await getFieldSettings(
        candidate?.isJobCandidateMapping != undefined ? candidate.isJobCandidateMapping : true
      );
      setSettings(req);
    })();
  }, [candidate.isJobCandidateMapping, getFieldSettings]);

  const isJobContract = allowedContractTypes.includes(candidate?.jobType ?? '');
  return (
    <>
      {settings && (
        <>
          {!isEmpty(candidate?.skills) && settings?.skills && settings?.skills.hidden === false && (
            <GetLabel mt={2.5} title="Skills">
              {!isEmpty(candidate.skills) ? (
                <>
                  {showAll ? (
                    <>
                      <Grid
                        container
                        direction="row"
                        spacing={3}
                        sx={{ maxWidth: '100%', overflowX: 'auto' }}
                      >
                        {candidate?.skills?.map((skill, index: number) => (
                          <Box key={index} marginBottom="2px" paddingTop="15px">
                            <WsChip value={skill} variant={WsChipVariants.PRIMARY} />
                          </Box>
                        ))}
                        <Box sx={{ float: 'right' }} mt={2.5}>
                          <WsTypography
                            onClick={() => setShowAll(false)}
                            cursor="pointer"
                            color={WsTypoColors.PRIMARY}
                          >
                            show less
                          </WsTypography>
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid
                        container
                        direction="row"
                        spacing={3}
                        sx={{ maxWidth: '100%', overflowX: 'auto' }}
                      >
                        {candidate?.skills?.slice(0, 5).map((skill, index: number) => (
                          <Box key={index} marginBottom="2px" paddingTop="15px">
                            <WsChip value={skill} variant={WsChipVariants.PRIMARY} />
                          </Box>
                        ))}
                        {candidate?.skills?.length > 5 && (
                          <Box sx={{ float: 'right' }} mt={2.5}>
                            <WsTypography
                              onClick={() => setShowAll(true)}
                              cursor="pointer"
                              color={WsTypoColors.PRIMARY}
                            >
                              {'+' + (candidate?.skills?.length - 5) + 'more'}
                            </WsTypography>
                          </Box>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              ) : (
                displayValue('Not added', 'Not added')
              )}
            </GetLabel>
          )}
          {!isEmpty(candidate?.currentJobTitle) &&
            ((settings?.jobTitle && settings?.jobTitle.hidden === false) ||
              (settings?.currentJobTitle && settings?.currentJobTitle.hidden === false)) && (
              <GetLabel title="Current Job Title">
                {displayValue(candidate.currentJobTitle, 'Not added')}
              </GetLabel>
            )}
          {!isEmpty(candidate?.currentEmployer ?? candidate?.companyName) &&
            ((settings?.currentCompany && settings?.currentCompany.hidden === false) ||
              (settings?.currentEmployer && settings?.currentEmployer.hidden === false)) && (
              <GetLabel title="Current Job Employer">
                {displayValue(candidate.currentEmployer ?? candidate.companyName, 'Not added')}
              </GetLabel>
            )}
          {settings?.previousJobTitles && settings?.previousJobTitles.hidden === false && (
            <>
              {!isEmpty(candidate.experience?.history) ||
                (!isEmpty(candidate.previousJobTitles) && (
                  <GetLabel title="Previous Job Title">
                    <CandidateWorkHistory candidate={candidate} mt={7} />
                  </GetLabel>
                ))}
            </>
          )}

          {!isEmpty(candidate?.previousEmployers) &&
            ((settings?.prevEmployers && settings?.prevEmployers.hidden === false) ||
              (settings?.previousEmployers && settings?.previousEmployers.hidden === false)) && (
              <GetLabel title="Previous Employers">
                {candidate.previousEmployers && candidate.previousEmployers.length > 0 && (
                  <>
                    <Label sx={{ mt: '7px' }}></Label>
                    {candidate.previousEmployers.map((title, index) => (
                      <WsTypography key={index} bold={true} display="inline">
                        {`${index > 0 ? `, ${title}` : title}`}
                      </WsTypography>
                    ))}
                  </>
                )}
              </GetLabel>
            )}

          {!isEmpty(candidate?.educationQualifications) &&
            settings?.highestEducation &&
            settings?.highestEducation.hidden === false && (
              <GetLabel title="Highest Education Qualifications">
                <Label sx={{ mt: '7px' }}></Label>
                {candidate?.educationQualifications?.map((education, index: number) => (
                  <WsTypography key={index} bold={true}>
                    {education}
                  </WsTypography>
                ))}
              </GetLabel>
            )}

          {!isEmpty(candidate?.institutionsAttended) &&
            settings?.institutesAttended &&
            settings?.institutesAttended.hidden === false && (
              <GetLabel title="Institutions Attended">
                {isEmpty(candidate?.institutionsAttended) ? (
                  <>
                    {candidate?.institutionsAttended?.map((value) =>
                      displayValue(value, 'Not added')
                    )}
                  </>
                ) : (
                  <>
                    <Label sx={{ mt: '7px' }}></Label>
                    <WsTypography bold={true}>{'Not added'}</WsTypography>
                  </>
                )}
              </GetLabel>
            )}
          {((settings?.jobIncludesTravel && settings?.jobIncludesTravel.hidden === false) ||
            (settings?.currentJobIncludeTravel &&
              settings?.currentJobIncludeTravel.hidden === false) ||
            !isEmpty(candidate.jobIncludesTravel)) && (
            <GetLabel mt={0} title="Current Job Includes Travel">
              {displayValue(candidate?.jobIncludesTravel ? 'Yes' : 'No', 'Not added')}
            </GetLabel>
          )}
          {((settings?.willingToTravel && settings?.willingToTravel.hidden === false) ||
            !isEmpty(candidate.willingToTravel)) && (
            <GetLabel title="Willing to Travel">
              {displayValue(candidate?.willingToTravel ? 'Yes' : 'No', 'Not added')}
            </GetLabel>
          )}
          {!isEmpty(candidate.willingToTravelType) &&
            ((settings?.travelPreference && settings?.travelPreference.hidden === false) ||
              !isEmpty(candidate.willingToTravelType)) && (
              <GetLabel title="Travel Preferences">
                {displayValue(candidate?.willingToTravelType, 'Not added')}
              </GetLabel>
            )}
          {!isEmpty(candidate.willingToRelocate) && (
            <GetLabel title="Willing to Relocate">
              {displayValue(candidate?.willingToRelocate ? 'Yes' : 'No', 'Not added')}
            </GetLabel>
          )}
          {!isEmpty(candidate?.relocationPreference) &&
            ((settings?.relocationPreference && settings?.relocationPreference.hidden === false) ||
              (settings?.relocationPreferences &&
                settings?.relocationPreferences.hidden === false)) && (
              <GetLabel title="Relocation Preferences">
                {displayValue(candidate?.relocationPreference, 'Not added')}
              </GetLabel>
            )}
          {((settings?.familyWillingToRelocate &&
            settings?.familyWillingToRelocate.hidden === false) ||
            !isEmpty(candidate.familyWillingToRelocate)) && (
            <GetLabel title="Family Willing to Relocate">
              <Label sx={{ mt: '7px' }}></Label>
              <WsTypography bold={true}>
                {candidate?.familyWillingToRelocate == true ? 'Yes' : 'No'}
              </WsTypography>
            </GetLabel>
          )}
          {!isEmpty(candidate?.noticePeriodInDays) &&
            ((settings?.noticePeriodInDays && settings?.noticePeriodInDays.hidden === false) ||
              (settings?.noticePeriod && settings?.noticePeriod.hidden === false)) && (
              <GetLabel title="Notice Period">
                {displayValue(candidate?.noticePeriodInDays, 'Not added')}
              </GetLabel>
            )}
          {(!isEmpty(candidate.commPreferencePhone) || !isEmpty(candidate.commPreferenceEmail)) &&
            ((settings?.communicationPreference &&
              settings?.communicationPreference.hidden === false) ||
              (settings?.communicationPreferences &&
                settings?.communicationPreferences.hidden === false) ||
              !isEmpty(candidate.commPreferencePhone) ||
              !isEmpty(candidate.commPreferenceEmail)) && (
              <GetLabel title="Communication Preference">
                <Box mt={1}>
                  {candidate?.commPreferencePhone && <WsTypography bold={true}>Phone</WsTypography>}
                </Box>
                <Box mt={1}>
                  {candidate?.commPreferenceEmail && <WsTypography bold={true}>Email</WsTypography>}
                </Box>
              </GetLabel>
            )}
          {(!isEmpty(candidate.jobPreferenceContract) ||
            !isEmpty(candidate.jobPreferencePermanent) ||
            !isEmpty(candidate?.preferredJobTypes)) &&
            ((settings?.jobPreference && settings?.jobPreference.hidden === false) ||
              (settings?.jobPreferences && settings?.jobPreferences.hidden === false)) && (
              <GetLabel title="Job Preference">
                <>
                  {(candidate.jobPreferenceContract || candidate.jobPreferencePermanent) && (
                    <>
                      <Box mt={1}>
                        {candidate.jobPreferenceContract && (
                          <WsTypography bold={true}>Contract</WsTypography>
                        )}
                      </Box>
                      <Box mt={1}>
                        {candidate.jobPreferencePermanent && (
                          <WsTypography bold={true}>Permanent</WsTypography>
                        )}
                      </Box>
                    </>
                  )}
                  {!isEmpty(candidate.preferredJobTypes) && (
                    <>
                      {candidate?.preferredJobTypes?.map((jobType, index: number) => (
                        <WsTypography key={index} bold={true}>
                          {jobType}
                        </WsTypography>
                      ))}
                    </>
                  )}
                </>
              </GetLabel>
            )}

          {!isEmpty(candidate.availableFrom) &&
            settings?.availableFrom &&
            settings?.availableFrom.hidden === false &&
            candidate.availableFrom && (
              <GetLabel title="Available From">
                <Box mt={1}>
                  <WsTypography bold={true}>{formattedDate(candidate.availableFrom)}</WsTypography>
                </Box>
              </GetLabel>
            )}

          {!isEmpty(candidate?.desiredJobTitles) && (
            <GetLabel title="Preferred Job Title">
              {candidate?.desiredJobTitles?.map((job, index: number) => (
                <WsTypography key={index} bold={true}>
                  {job}
                </WsTypography>
              ))}
            </GetLabel>
          )}

          {!isEmpty(candidate.preferredLocations) &&
            settings?.preferredLocation &&
            settings?.preferredLocation.hidden === false && (
              <GetLabel title="Preferred Location">
                <>
                  <Label sx={{ mt: '7px' }}></Label>
                  {candidate?.preferredLocations?.map((location, index: number) => (
                    <WsTypography key={index} bold={true}>
                      {formatLocation(location)}
                    </WsTypography>
                  ))}
                </>
              </GetLabel>
            )}

          {!isEmpty(candidate?.currentJobType) &&
            ((settings?.jobType && settings?.jobType.hidden === false) ||
              (settings?.currentJobType && settings?.currentJobType.hidden === false)) && (
              <GetLabel title="Current Job Type">
                {displayValue(candidate?.currentJobType, 'Not added')}
              </GetLabel>
            )}
          {candidate.currentSalary !== null &&
            candidate.currentSalary > 0 &&
            ((settings?.currentCompensation && !settings.currentCompensation.hidden) ||
              (settings?.currentSalary && !settings.currentSalary.hidden)) && (
              <GetLabel title="Current Salary">
                <Label sx={{ mt: '7px' }}></Label>
                <WsTypography bold={true}>{getSalary('current', candidate)}</WsTypography>
              </GetLabel>
            )}

          {!isEmpty(candidate?.otherCurrentAllowances) &&
            settings?.otherAllowance &&
            settings?.otherAllowance.hidden === false && (
              <GetLabel title="Allowances & Benefits">
                {displayValue(candidate?.otherCurrentAllowances, 'Not added')}
              </GetLabel>
            )}
          {candidate.expectedBaseSalary !== null &&
            candidate.expectedBaseSalary > 0 &&
            ((settings?.expectedCompensation && settings?.expectedCompensation.hidden === false) ||
              (settings?.expectedSalary && settings?.expectedSalary.hidden === false)) && (
              <GetLabel title={isJobContract ? 'Submitted Payrate' : 'Submitted Compensation'}>
                <Label sx={{ mt: '7px' }}></Label>
                <WsTypography bold={true}>{getSalary('expected', candidate)}</WsTypography>
              </GetLabel>
            )}
          {!isEmpty(candidate?.otherExpectedAllowances) &&
            settings?.expectedAllowance &&
            settings?.expectedAllowance.hidden === false && (
              <GetLabel title="Expected Allowances & Benefits">
                {displayValue(candidate?.otherExpectedAllowances, 'Not added')}
              </GetLabel>
            )}
          {candidate?.likelyToMove == true && (
            <GetLabel title="Likely to Switch">
              {displayValue(candidate?.likelyToMove == true ? 'Yes' : 'No', 'Not added')}
            </GetLabel>
          )}
          {candidate?.likelyToMove == true && (
            <GetLabel title="Likely to Move score">
              {displayValue(String(candidate?.likelyToMoveScore), 'Not added')}
            </GetLabel>
          )}
          {((settings?.interviewingWithOthers &&
            settings?.interviewingWithOthers.hidden === false) ||
            (settings?.interviewingAnywhereElse &&
              settings?.interviewingAnywhereElse.hidden === false) ||
            !isEmpty(candidate.interviewingWithOthers)) && (
            <GetLabel mt={0} title="Interviewing with others">
              {displayValue(candidate?.interviewingWithOthers ? 'Yes' : 'No', 'Not added')}
            </GetLabel>
          )}
          {!isEmpty(candidate?.otherInterviewStage) &&
            ((settings?.otherInterviewStage && settings.otherInterviewStage.hidden === false) || (
              <GetLabel title="Other Interview Stage">
                {displayValue(candidate?.otherInterviewStage, 'Not added')}
              </GetLabel>
            ))}
          {!isEmpty(candidate?.reasonForNewJob) &&
            ((settings?.jobChangeReason && settings?.jobChangeReason.hidden === false) ||
              (settings?.reasonForChange && settings?.reasonForChange.hidden === false)) && (
              <GetLabel title="Reason looking for a new job">
                {displayValue(candidate?.reasonForNewJob, 'Not added')}
              </GetLabel>
            )}
          {!isEmpty(candidate?.assessment) &&
            ((settings?.recruiterAssessment && settings?.recruiterAssessment.hidden === false) ||
              (settings?.yourAssessment && settings?.yourAssessment.hidden === false)) && (
              <GetLabel title="Recruiters assessment">
                {displayValue(candidate?.assessment, 'Not added')}
              </GetLabel>
            )}
          <GetLabel title="I have spoken to the Candidate for their interest in this Position and have obtained the Right to Represent">
            <Label sx={{ mt: '7px' }}></Label>
            <WsTypography bold={true}>{candidate?.rtrConsent ? 'Yes' : 'N/A'}</WsTypography>
          </GetLabel>
        </>
      )}
    </>
  );
};

export default UpdatedDetailsTimeline;
